import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterSidebar from '../components/FilterSidebar';
import SEO from '../components/SEO';
import type { Trip, TripsResponse } from '../types/trip';

const skeletonRows = Array.from({ length: 12 }, (_, index) => index);

export default function TripsPage() {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const limit = 12;

  useEffect(() => {
    const loadTrips = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (countryFilter) {
          params.set('country', countryFilter);
        }
        params.set('limit', String(limit));
        params.set('offset', String((currentPage - 1) * limit));
        
        console.log('🟢 [Trips] Fetching:', `/api/trips?${params}`);
        
        const response = await fetch(`/api/trips?${params}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch trips');
        }
        
        const data: TripsResponse = await response.json();
        setTrips(data.trips ?? []);
        setTotalItems(data.total ?? 0);
        if (data.countries?.length) {
          setCountries(data.countries);
        }
      } catch (err: any) {
        console.error('🔴 [Trips] Error:', err);
        setError('Unable to load trips. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    loadTrips();
  }, [countryFilter, currentPage]);

  const totalPages = Math.ceil(totalItems / limit);

  const handleResetFilters = () => {
    setCountryFilter('');
    setCurrentPage(1);
  };

  return (
    <div className="flex-1 bg-white">
      <SEO
        title="Discover Curated Travel Trips & Itineraries | Waybook"
        description="Browse curated travel trips and itineraries from creators worldwide. Find your next adventure with detailed travel plans and recommendations."
        keywords="travel trips, travel itineraries, curated trips, travel plans, vacation planning, trip ideas"
        canonicalUrl="https://www.waybook.me/recommended/trips"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Curated Travel Trips',
          description: 'Browse curated travel trips and itineraries from creators worldwide',
          url: 'https://www.waybook.me/recommended/trips'
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <FilterSidebar
            countries={countries}
            typeFilter={countryFilter}
            onTypeChange={setCountryFilter}
            onReset={handleResetFilters}
          />

          {/* Main Content */}
          <main className="flex-1">
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                <h1 className="text-3xl font-bold text-black">All Trips</h1>
                <p className="text-sm text-gray-600">
                  {totalItems > 0 ? `${totalItems} trips found` : 'No trips found'}
                </p>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {skeletonRows.map((row) => (
                    <div
                      key={`skeleton-${row}`}
                      className="border border-gray-200 bg-white animate-pulse"
                    >
                      <div className="h-64 bg-gray-200" />
                      <div className="p-4 space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-3/4" />
                        <div className="h-3 bg-gray-200 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="border border-gray-200 bg-white p-8 text-center">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              ) : !trips.length ? (
                <div className="border border-gray-200 bg-white p-8 text-center">
                  <p className="text-sm text-gray-500">No trips found for this filter.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {trips.map((trip) => {
                      const imageUrl = trip.cover_image_url || trip.image_url;
                      return (
                        <div
                          key={trip.id}
                          onClick={() => navigate(`/trip/${trip.id}`)}
                          className="border border-gray-200 bg-white hover:border-black transition-colors group cursor-pointer"
                        >
                          {imageUrl ? (
                            <div className="h-64 overflow-hidden bg-gray-100">
                              <img
                                src={imageUrl}
                                alt={trip.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              />
                            </div>
                          ) : (
                            <div className="h-64 bg-gray-200 flex items-center justify-center">
                              <span className="text-6xl font-bold text-gray-400">
                                {trip.title.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div className="p-4">
                            <h3 className="font-semibold text-base mb-1">{trip.title}</h3>
                            <p className="text-sm text-gray-600">{trip.type || 'Trip'}</p>
                            {trip.description && (
                              <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                                {trip.description}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-gray-200 pt-6">
                      <button
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="bg-black text-waybook-green px-6 py-2 text-sm font-semibold transition-colors hover:bg-gray-900 disabled:opacity-30 disabled:hover:bg-black"
                      >
                        Previous
                      </button>

                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          Page {currentPage} of {totalPages}
                        </span>
                      </div>

                      <button
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="bg-black text-waybook-green px-6 py-2 text-sm font-semibold transition-colors hover:bg-gray-900 disabled:opacity-30 disabled:hover:bg-black"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
